import React, { useState, useEffect } from 'react';

const Footer = ({ siteUrl = '/', siteLogo = null, siteTitle = 'ARENES', siteDescription = '' }) => {
  const currentYear = new Date().getFullYear();
  const [translations, setTranslations] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState('pt');

  // Translation helper
  const t = (key, fallback = key) => {
    return translations[key] || fallback;
  };

  useEffect(() => {
    const savedLang = localStorage.getItem('ghost-lang') || 'pt';
    setCurrentLanguage(savedLang);

    // Load translations
    const loadTranslations = async (lang) => {
      try {
        const url = window.ghostTranslationUrls?.[lang];
        if (!url) return;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setTranslations(data);
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };

    loadTranslations(savedLang);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLang = event.detail.lang;
      setCurrentLanguage(newLang);
      loadTranslations(newLang);
    };

    window.addEventListener('languageChange', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  const footerLinks = [
    { label: 'About', href: '/about/' },
    { label: 'Shows', href: '/shows/' },
    { label: 'Contact Page', href: '/contacts/' },
    { label: 'Listen', href: '/podcasts/' }
  ];

  const socialLinks = [
    { label: 'Facebook', href: '#', icon: 'facebook' },
    { label: 'TikTok', href: '#', icon: 'tiktok' },
    { label: 'Instagram', href: '#', icon: 'instagram' },
    { label: 'YouTube', href: '#', icon: 'youtube' },
    { label: 'Spotify', href: '#', icon: 'spotify' }
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy/' },
    { label: 'Terms of Service', href: '/terms-of-service/' },
    { label: 'Refund Policy', href: '/refund-policy/' },
    { label: 'Contact Page', href: '/contacts/' },
    { label: 'Equality, Diversity and Inclusion Policy', href: '/equality-diversity-and-inclusion-policy/' }
  ];

  return (
    <footer className="contemporary-footer">
      <style>{`
        .contemporary-footer {
          background: var(--surface-elevated);
          color: var(--text-primary);
          margin-top: clamp(4rem, 10vw, 8rem);
        }

        .contemporary-footer-inner {
          width: min(100%, 1880px);
          max-width: none;
          margin: 0 auto;
          padding: clamp(3rem, 6vw, 5rem) clamp(1rem, 2.4vw, 2.8rem);
        }

        .contemporary-footer-grid {
          display: grid;
          grid-template-columns: minmax(360px, 1.25fr) minmax(260px, 1fr) minmax(260px, 1fr);
          gap: clamp(1.2rem, 2.8vw, 3rem);
          margin-bottom: clamp(3rem, 6vw, 4rem);
          padding-bottom: clamp(3rem, 6vw, 4rem);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .contemporary-footer-brand {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .contemporary-footer-logo {
          font-size: 1.5rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--text-primary);
        }

        .contemporary-footer-logo-img {
          display: inline-block;
          margin-bottom: 0.5rem;
        }

        .contemporary-footer-logo-img img {
          height: 60px;
          width: auto;
          max-width: 300px;
        }

        .contemporary-footer-description {
          font-size: 1rem;
          line-height: 1.6;
          color: var(--text-secondary);
          max-width: 58ch;
        }

        .contemporary-footer-section-title {
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 1.5rem 0;
        }

        .contemporary-footer-links {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .contemporary-footer-links a {
          color: rgba(255, 255, 255, 0.9) !important;
          text-decoration: none;
          font-size: 0.9375rem;
          transition: color 0.2s ease;
        }

        .contemporary-footer-links a:hover {
          color: #fff;
        }

        .contemporary-footer-social {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .contemporary-footer-social a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: #ffffff; /* white tiles behind icons */
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 6px;
          color: #111827; /* near-black icons */
          text-decoration: none;
          transition: all 0.12s ease;
        }

        .contemporary-footer-social a svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
          stroke: currentColor;
        }

        .contemporary-footer-social a:hover {
          background: #f3f4f6; /* subtle hover */
          border-color: rgba(0, 0, 0, 0.12);
          color: #111827;
          transform: translateY(-2px);
        }

        .contemporary-footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
          gap: 2rem;
          flex-wrap: wrap;
        }

        .contemporary-footer-bottom a {
          color: rgba(255, 255, 255, 0.85) !important;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contemporary-footer-bottom a:hover {
          color: #fff;
        }

        .contemporary-footer-partners {
          display: flex;
          align-items: center;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .contemporary-footer-partners a {
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .contemporary-footer-partners a:hover {
          opacity: 1;
        }

        .contemporary-footer-partners img {
          display: block;
        }

        .contemporary-footer-legal {
          display: flex;
          gap: 2rem;
        }

        @media (max-width: 968px) {
          .contemporary-footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }

          .contemporary-footer-brand {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 640px) {
          .contemporary-footer-grid {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }

          .contemporary-footer-bottom {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .contemporary-footer-legal {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <div className="contemporary-footer-inner">
        <div className="contemporary-footer-grid">
          <div className="contemporary-footer-brand">
            {siteLogo ? (
              <a href={siteUrl} className="contemporary-footer-logo-img">
                <img src={siteLogo} alt={siteTitle} width="300" height="72" />
              </a>
            ) : (
              <div className="contemporary-footer-logo">{siteTitle}</div>
            )}
            <p className="contemporary-footer-description">
              {siteDescription}
            </p>
            <div className="contemporary-footer-social">
              {socialLinks.map((link) => (
                <a
                  key={link.icon}
                  href={link.href}
                  aria-label={`${t('Follow us on', 'Siga-nos no')} ${t(link.label, link.label)}`}
                  title={t(link.label, link.label)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.icon === 'facebook' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  )}
                  {link.icon === 'tiktok' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"></path>
                    </svg>
                  )}
                  {link.icon === 'instagram' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  )}
                  {link.icon === 'youtube' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                    </svg>
                  )}
                  {link.icon === 'spotify' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 14.5c-.17.28-.54.37-.82.2-2.24-1.37-5.06-1.68-8.38-.92-.3.07-.6-.1-.67-.4-.07-.3.1-.6.4-.67 3.63-.83 6.74-.47 9.27 1.07.28.17.37.54.2.82zm1.17-2.63c-.22.35-.68.46-1.03.24-2.57-1.58-6.48-2.04-9.51-1.12-.36.11-.74-.08-.85-.44-.11-.36.08-.74.44-.85 3.47-1.06 7.8-.55 10.7 1.28.35.22.46.68.24 1.03zm.1-2.75c-3.08-1.83-8.16-2-11.1-1.11-.43.13-.88-.11-1.01-.54-.13-.43.11-.88.54-1.01 3.39-1.02 8.98-.82 12.48 1.28.4.24.53.76.29 1.16-.24.4-.76.53-1.16.29z"></path>
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="contemporary-footer-section-title">{t('Legal & Info', 'Legal & Informações')}</h3>
            <ul className="contemporary-footer-links">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href}>{t(link.label, link.label)}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="contemporary-footer-section-title">{t('Contact Page', 'Página de Contacto')}</h3>
            <ul className="contemporary-footer-links">
              <li>
                <a href="mailto:garcia@joaogarciamiguel.com">
                  garcia@joaogarciamiguel.com
                </a>
              </li>
              <li>
                <a href="tel:+351933326207">
                  +351 933 326 207
                </a>
              </li>
              <li>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                  Torres Vedras, Portugal
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="contemporary-footer-bottom">
          <div>
            © {currentYear} {siteTitle}. {t('Published with', 'Publicado com')} <a href="https://ghost.org/" target="_blank" rel="noopener">Ghost</a>
          </div>
          <div className="contemporary-footer-partners">
            <a href="https://www.culturaportugal.gov.pt/" target="_blank" rel="noopener" aria-label="República Portuguesa Cultura">
              <img src="/assets/images/REPUBLICA-branco.png" alt="República Portuguesa Cultura Logo" style={{ height: '40px', width: 'auto' }} />
            </a>
            <a href="https://www.dgartes.gov.pt/" target="_blank" rel="noopener" aria-label="DGArtes">
              <img src="/assets/images/DGArtes.png" alt="DGArtes Logo" style={{ height: '40px', width: 'auto' }} />
            </a>
            <a href="#" target="_blank" rel="noopener" aria-label="The Green Book">
              <img src="/assets/images/250623.png" alt="The Green Book" style={{ height: '40px', width: 'auto' }} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
